import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { toast } from "@/components/ui/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import { JournalEntryDTO } from "@/types/journalEntry";
import { journalEntryService } from "@/services/journalEntryService";
import JournalEntryItem from "@/components/admin/journal/JournalEntryItem";
import {
  JournalActionFields,
  JournalActionValue,
  toReminderIso,
} from "@/components/admin/journal/JournalActionFields";
import { BackLink } from "@/components/brand/BackLink";

const PAGE_SIZE = 20;

export default function EventJournal() {
  const { id } = useParams();

  const [entries, setEntries] = useState<JournalEntryDTO[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [eventStartTime, setEventStartTime] = useState<string | null>(null);

  const [newContent, setNewContent] = useState("");
  const [newFollowUp, setNewFollowUp] = useState<JournalActionValue>({
    action: "",
    reminderAt: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchEntries = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await journalEntryService.list({
        eventId: id,
        page,
        size: PAGE_SIZE,
      });
      setEntries(data.content);
      setTotalPages(data.page.totalPages);
    } catch {
      toast({
        title: "Failed to load journal entries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  // Fetch the event header separately so the title shows even with no entries yet.
  const fetchEventHeader = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`event/${id}`);
      setCompanyName(res.data?.companyName ?? "");
      setEventStartTime(res.data?.startTime ?? null);
    } catch {
      // Non-fatal: the header just falls back to a generic label.
    }
  }, [id]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    fetchEventHeader();
  }, [fetchEventHeader]);

  const handleAdd = async () => {
    if (!id || !newContent.trim()) {
      toast({ title: "Entry can't be empty", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await journalEntryService.create({
        eventId: id,
        content: newContent.trim(),
        action: newFollowUp.action.trim() || null,
        reminderAt: toReminderIso(newFollowUp.reminderAt),
      });
      setNewContent("");
      setNewFollowUp({ action: "", reminderAt: "" });
      toast({
        title: "Entry added",
        description: newFollowUp.reminderAt
          ? "The admins will be reminded at the time you picked."
          : undefined,
      });
      if (page !== 0) {
        setPage(0); // jump to newest; triggers refetch
      } else {
        fetchEntries();
      }
    } catch {
      toast({ title: "Failed to add entry", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdated = (updated: JournalEntryDTO) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const handleDeleted = (deletedId: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== deletedId));
  };

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-3">
          <BackLink to={`/admin/events/${id}`}>Back to Event</BackLink>
          <div>
            <h1 className="text-2xl font-bold">Journal</h1>
            <p className="text-muted-foreground">
              {companyName || "Event"}
              {eventStartTime &&
                ` · ${format(new Date(eventStartTime), "MMM d, yyyy")}`}
            </p>
          </div>
        </div>

        {/* Quick-add */}
        <Card className="mb-0">
          <CardContent className="p-3 space-y-2">
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Add a note about this event…"
              rows={3}
            />
            <JournalActionFields
              idPrefix="new-entry"
              value={newFollowUp}
              onChange={setNewFollowUp}
              disabled={saving}
            />
            <Button
              onClick={handleAdd}
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add Entry
            </Button>
          </CardContent>
        </Card>

        {/* Entries */}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">
            No journal entries yet.
          </p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <JournalEntryItem
                key={entry.id}
                entry={entry}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        )}

        <DataTablePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
